import { useRef, useState } from "react";
import { toast } from "sonner";
import { Check, Plus, X } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiErrorDetail } from "@/lib/api";

const Field = ({ label, hint, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs uppercase tracking-widest text-zinc-500">{label}</Label>
    {children}
    {hint && <p className="text-[11px] text-zinc-500">{hint}</p>}
  </div>
);

const PROMPTS = [
  { key: "ask", label: "The ask", starter: "The ask — " , hint: "What the customer wanted and why" },
  { key: "stone", label: "The stone choice", starter: "The stone — ", hint: "Why this slab, how you matched veins" },
  { key: "tricky", label: "The tricky part", starter: "The tricky part — ", hint: "Template, cut or install challenge and how you solved it" },
];

// Office-side case-study editor for one finished photo: title, room, short line, story, edge, sink.
export const ProjectStoryDialog = ({ item, onClose, onSaved }) => {
  const [f, setF] = useState({
    name: item.name || "",
    category: item.category || "",
    description: item.description || "",
    story: item.story || "",
    edge_profile: item.edge_profile || "",
    sink: item.sink || "",
  });
  const [busy, setBusy] = useState(false);
  const storyRef = useRef(null);
  const set = (patch) => setF((v) => ({ ...v, ...patch }));

  // drop a prompt starter in as its own paragraph and put the cursor after it
  const addPrompt = (p) => {
    const cur = f.story.trimEnd();
    const next = `${cur ? `${cur}\n\n` : ""}${p.starter}`;
    set({ story: next });
    requestAnimationFrame(() => {
      const el = storyRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(next.length, next.length);
      el.scrollTop = el.scrollHeight;
    });
  };
  const used = (p) => f.story.includes(p.starter.trim());

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.patch(`/completion-photos/${item.id}`, f);
      toast.success("Project story saved");
      onSaved(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save");
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/80 p-4 sm:items-center" data-testid="project-story-dialog" onClick={onClose}>
      <form
        onSubmit={save}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl rounded-lg border border-zinc-800 bg-zinc-900 p-6 shadow-2xl"
        data-testid="project-story-form"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-bold">Project story</h2>
            <p className="mt-1 text-xs text-zinc-500">
              What shows on the public project page — write it like a short case study.
            </p>
          </div>
          <button type="button" onClick={onClose} data-testid="project-story-close" className="text-zinc-500 hover:text-white">
            <X size={18} weight="bold" />
          </button>
        </div>

        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <Field label="Project title">
            <Input data-testid="project-story-name" value={f.name} onChange={(e) => set({ name: e.target.value })} placeholder="Maplewood kitchen" className="h-11 border-zinc-800 bg-zinc-950" />
          </Field>
          <Field label="Room / category">
            <Input data-testid="project-story-category" value={f.category} onChange={(e) => set({ category: e.target.value })} placeholder="Kitchen" className="h-11 border-zinc-800 bg-zinc-950" />
          </Field>
          <Field label="Edge profile">
            <Input data-testid="project-story-edge" value={f.edge_profile} onChange={(e) => set({ edge_profile: e.target.value })} placeholder="Mitred waterfall, 2¼″ apron" className="h-11 border-zinc-800 bg-zinc-950" />
          </Field>
          <Field label="Sink">
            <Input data-testid="project-story-sink" value={f.sink} onChange={(e) => set({ sink: e.target.value })} placeholder="Undermount 32″ single bowl, polished cut-out" className="h-11 border-zinc-800 bg-zinc-950" />
          </Field>
          <div className="sm:col-span-2">
            <Field label="One-line summary" hint="Shows under the title and on the gallery tile.">
              <Input data-testid="project-story-description" value={f.description} onChange={(e) => set({ description: e.target.value })} placeholder="Waterfall island, mitred edge, vein matched" className="h-11 border-zinc-800 bg-zinc-950" />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="The story" hint="Blank line between paragraphs. Tap a prompt to start the next paragraph — three prompts, one consistent case study.">
              <div className="mb-2 flex flex-wrap gap-2" data-testid="project-story-prompts">
                {PROMPTS.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    data-testid={`project-story-prompt-${p.key}`}
                    onClick={() => addPrompt(p)}
                    title={p.hint}
                    className={`flex h-8 items-center gap-1.5 rounded-md border px-3 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                      used(p)
                        ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-300"
                        : "border-zinc-700 text-zinc-400 hover:border-zinc-500 hover:text-zinc-100"
                    }`}
                  >
                    {used(p) ? <Check size={11} weight="bold" /> : <Plus size={11} weight="bold" />}
                    {p.label}
                  </button>
                ))}
              </div>
              <Textarea
                ref={storyRef}
                data-testid="project-story-text"
                value={f.story}
                onChange={(e) => set({ story: e.target.value })}
                rows={9}
                placeholder={"The ask — the homeowners wanted the island to read as one block of stone…\n\nThe stone — we book-matched two slabs so the veins run over the waterfall…\n\nThe tricky part — the mitre had to disappear from the breakfast bar…"}
                className="border-zinc-800 bg-zinc-950"
              />
            </Field>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap justify-end gap-3">
          <Button type="button" variant="outline" onClick={onClose} data-testid="project-story-cancel" className="h-11 border-zinc-700 bg-transparent text-[11px] uppercase tracking-widest hover:bg-zinc-800">
            Cancel
          </Button>
          <Button type="submit" disabled={busy} data-testid="project-story-save" className="h-11 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400">
            {busy ? "Saving…" : "Save story"}
          </Button>
        </div>
      </form>
    </div>
  );
};
